import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Archive,
  ArrowRight,
  Check,
  Copy,
  Edit3,
  Eye,
  FileText,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import DocumentRequestQueue from '../../components/documentRequests/DocumentRequestQueue';
import ConfirmModal from '../../components/UI/ConfirmModal';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import FormSelect from '../../components/UI/FormSelect';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Modal from '../../components/UI/Modal';
import PdfViewer from '../../components/UI/PdfViewer';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import {
  useArchiveDocumentTemplate,
  useDeleteDocumentTemplate,
  useDocumentRequestQueue,
  useDocumentTemplateMergeFields,
  useDocumentTemplatePreview,
  useDocumentTemplates,
  useDocumentTypes,
  usePublishDocumentTemplate,
  useSaveDocumentTemplate,
} from '../../hooks/useDocumentRequestData';
import { useTabPersistence } from '../../hooks/usePersistence';
import { formatDate, getTodayInSingaporeTimezone } from '../../utils/dateUtils';
import toast from '../../utils/toast';

const EMPTY_TEMPLATE = {
  version: 1,
  document_type: '',
  language: 'en',
  engine: 'docx',
  variables_schema: { required: [], optional: [] },
  allow_include_salary: false,
  docx_file: null,
  effective_from: getTodayInSingaporeTimezone(),
};

const templateTypeLabel = (template) =>
  template.document_type_name
  || template.document_type?.name
  || template.name
  || 'Document template';

const templateStatusBadge = (status) => {
  if (status === 'published') {
    return <StatusBadge status="completed" label="Published" />;
  }
  if (status === 'archived') {
    return <StatusBadge status="cancelled" label="Archived" />;
  }
  return <StatusBadge status="draft" />;
};

const TemplateActions = ({
  template,
  onEdit,
  onPreview,
  onPublish,
  onArchive,
  onDelete,
}) => (
  <div className="inline-flex items-center gap-2">
    {template.status === 'draft' && (
      <button
        type="button"
        onClick={() => onEdit(template)}
        className="icon-button-admin"
        title="Edit template"
        aria-label="Edit template"
      >
        <Edit3 className="w-4 h-4" />
      </button>
    )}
    <button
      type="button"
      onClick={() => onPreview(template)}
      className="icon-button-admin"
      title="Preview template"
      aria-label="Preview template"
    >
      <Eye className="w-4 h-4" />
    </button>
    {template.status === 'draft' && (
      <>
        <button
          type="button"
          onClick={() => onPublish(template)}
          className="icon-button-admin"
          title="Publish template"
          aria-label="Publish template"
        >
          <Check className="w-4 h-4 text-green-600" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(template)}
          className="icon-button-danger"
          title="Delete draft"
          aria-label="Delete draft"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </>
    )}
    {template.status === 'published' && (
      <button
        type="button"
        onClick={() => onArchive(template)}
        className="icon-button-admin"
        title="Archive template"
        aria-label="Archive template"
      >
        <Archive className="w-4 h-4 text-amber-600" />
      </button>
    )}
  </div>
);

const FieldLibrary = ({ fields }) => {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState('');
  const filteredFields = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return fields;
    return fields.filter(
      (field) =>
        field.label?.toLowerCase().includes(normalized)
        || field.token?.toLowerCase().includes(normalized),
    );
  }, [fields, query]);

  const copyToken = async (field) => {
    try {
      await navigator.clipboard.writeText(field.token);
      setCopied(field.key);
      toast.success('Merge field copied');
      window.setTimeout(() => setCopied(''), 1200);
    } catch {
      toast.error('Could not copy the merge field');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Merge field library</h4>
        <p className="text-xs text-gray-500 mt-1">
          Copy these exact tokens into the DOCX template.
        </p>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search merge fields..."
          className="input-admin w-full pl-9"
        />
      </div>
      {filteredFields.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No merge fields found
          </h3>
          <p className="text-sm text-gray-500">Try a different search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filteredFields.map((field) => (
            <button
              key={field.key}
              type="button"
              onClick={() => copyToken(field)}
              className="w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2 text-left border border-gray-200 rounded-xl hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <span className="min-w-0">
                <span className="block text-sm text-gray-800 truncate">
                  {field.label}
                </span>
                <code className="block text-xs text-gray-500 truncate mt-0.5">
                  {field.token}
                </code>
              </span>
              {copied === field.key
                ? <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                : <Copy className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TemplateEditor = ({
  open,
  template,
  documentTypes,
  mergeFields,
  saving,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [step, setStep] = useState(1);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(
      template
        ? {
          ...EMPTY_TEMPLATE,
          ...template,
          document_type: String(
            template.document_type?.id || template.document_type || '',
          ),
          docx_file: null,
        }
        : EMPTY_TEMPLATE,
    );
    setStep(template ? 2 : 1);
    setDragging(false);
  }, [open, template]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Select a DOCX template file');
      return;
    }
    setField('docx_file', file);
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="btn btn-admin-outline"
      >
        Cancel
      </button>
      {step === 2 && !template && (
        <button
          type="button"
          onClick={() => setStep(1)}
          disabled={saving}
          className="btn btn-admin-outline"
        >
          Back
        </button>
      )}
      {step === 1 ? (
        <button
          type="button"
          onClick={() => setStep(2)}
          className="btn btn-admin-primary"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={saving}
          className="btn btn-admin-primary"
        >
          {saving ? <LoadingSpinner size="sm" /> : <Check className="w-4 h-4" />}
          Save template
        </button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={template ? 'Edit template' : 'Create document template'}
      size="xl"
      theme="admin"
      closable={!saving}
      footer={footer}
    >
      <div className="space-y-6">
        {!template && (
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
              }`}>
                1
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Merge fields</p>
                <p className="text-xs text-gray-500">Prepare the DOCX</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-3 justify-end">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 2 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
              }`}>
                2
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Template details</p>
                <p className="text-xs text-gray-500">Upload and configure</p>
              </div>
            </div>
          </div>
        )}

        {step === 1 ? (
          <FieldLibrary fields={mergeFields} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Document type *
              </label>
              <FormSelect
                value={form.document_type}
                onChange={(value) => setField('document_type', value)}
                options={[
                  { value: '', label: 'Select document type...' },
                  ...documentTypes.map((type) => ({
                    value: String(type.id),
                    label: type.name,
                  })),
                ]}
                theme="admin"
                required
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="template-version"
                className="text-sm font-medium text-gray-700"
              >
                Version *
              </label>
              <input
                id="template-version"
                type="number"
                min={1}
                step={1}
                value={form.version}
                onChange={(event) =>
                  setField('version', Math.max(1, Number(event.target.value) || 1))}
                className="input-admin w-full"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="template-effective-from"
                className="text-sm font-medium text-gray-700"
              >
                Effective from *
              </label>
              <input
                id="template-effective-from"
                type="date"
                value={form.effective_from}
                onChange={(event) =>
                  setField('effective_from', event.target.value)}
                className="input-admin w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Rendering engine
              </label>
              <div className="input-admin w-full bg-gray-50 text-gray-600">
                DOCX to PDF
              </div>
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-medium text-gray-700">
                DOCX template {!template && '*'}
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  setFile(event.dataTransfer.files?.[0]);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                className={`w-full min-h-[120px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 px-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
                  dragging
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  {form.docx_file?.name || 'Choose or drop a DOCX file'}
                </span>
                <span className="text-xs text-gray-500">
                  {template && !form.docx_file
                    ? 'Leave unchanged to keep the current file'
                    : 'Only .docx files are supported'}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={(event) => setFile(event.target.files?.[0])}
              />
            </div>

            <label className="md:col-span-2 min-h-[44px] inline-flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.allow_include_salary}
                onChange={(event) =>
                  setField('allow_include_salary', event.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Template supports salary details
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Enable only when the approved wording includes the salary merge field.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
};

const TemplatePreviewModal = ({ template, onClose }) => {
  const preview = useDocumentTemplatePreview(template?.id, !!template);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!preview.data) {
      setBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(
      new Blob([preview.data], { type: 'application/pdf' }),
    );
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview.data]);

  return (
    <Modal
      isOpen={!!template}
      onClose={onClose}
      title={template ? `Preview · ${templateTypeLabel(template)}` : 'Template preview'}
      size="lg"
      theme="admin"
      className="h-[92vh]"
      contentClassName="!p-0 !overflow-hidden bg-gray-100 flex flex-col min-h-0"
    >
      {preview.isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : preview.isError || !blobUrl ? (
        <div className="text-center py-12 px-6">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Preview unavailable
          </h3>
          <p className="text-sm text-gray-500">
            Check that the DOCX file is valid and try again.
          </p>
          <button
            type="button"
            onClick={() => preview.refetch()}
            className="btn btn-admin-primary mt-4"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <PdfViewer
            src={blobUrl}
            title={templateTypeLabel(template)}
            fileName={`${template.code || 'template'}_v${template.version}.pdf`}
            theme="admin"
          />
        </div>
      )}
    </Modal>
  );
};

const TemplatesTab = () => {
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState({ open: false, template: null });
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useDocumentTemplates();
  const { data: documentTypes = [] } = useDocumentTypes();
  const { data: mergeFields = [] } = useDocumentTemplateMergeFields();
  const save = useSaveDocumentTemplate();
  const publish = usePublishDocumentTemplate();
  const archive = useArchiveDocumentTemplate();
  const remove = useDeleteDocumentTemplate();

  const filteredTemplates = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return templates;
    return templates.filter((template) =>
      [
        template.name,
        template.document_type_name,
        template.code,
        template.status,
        template.version,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase()
        .includes(normalized));
  }, [query, templates]);

  const saveTemplate = async (form) => {
    if (!form.document_type) {
      toast.error('Select a document type');
      return;
    }
    if (!form.effective_from) {
      toast.error('Set the effective date');
      return;
    }
    if (!editor.template && !form.docx_file) {
      toast.error('Upload a DOCX template');
      return;
    }

    const documentType = documentTypes.find(
      (type) => String(type.id) === String(form.document_type),
    );
    const payload = {
      ...form,
      document_type: form.document_type,
      language: 'en',
      engine: 'docx',
    };
    if (!payload.docx_file) delete payload.docx_file;
    if (!payload.code) {
      payload.code = String(documentType?.name || 'template')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 80);
    }

    try {
      await save.mutateAsync({ id: editor.template?.id, data: payload });
      setEditor({ open: false, template: null });
    } catch {
      // Mutation owns the error toast; keep the editor open for correction.
    }
  };

  const handleConfirm = async () => {
    if (!confirm?.template) return;
    try {
      if (confirm.type === 'publish') {
        await publish.mutateAsync(confirm.template.id);
      } else if (confirm.type === 'archive') {
        await archive.mutateAsync(confirm.template.id);
      } else {
        await remove.mutateAsync(confirm.template.id);
      }
    } catch {
      return;
    }
    setConfirm(null);
  };

  const actionProps = {
    onEdit: (template) => setEditor({ open: true, template }),
    onPreview: setPreviewTemplate,
    onPublish: (template) => setConfirm({ type: 'publish', template }),
    onArchive: (template) => setConfirm({ type: 'archive', template }),
    onDelete: (template) => setConfirm({ type: 'delete', template }),
  };

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
              Letter templates
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Manage the controlled DOCX versions used for employee letters.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditor({ open: true, template: null })}
            className="btn btn-admin-primary self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            New template
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <DebouncedSearchInput
              value={query}
              onSearch={setQuery}
              placeholder="Search templates..."
              theme="admin"
              className="w-full sm:max-w-xl"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : isError ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Templates could not be loaded
              </h3>
              <p className="text-sm text-gray-500">Try loading the list again.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="btn btn-admin-primary mt-4"
              >
                Try again
              </button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No templates found
              </h3>
              <p className="text-sm text-gray-500">
                {query
                  ? 'Try a different search.'
                  : 'Create a DOCX template to enable employee requests.'}
              </p>
            </div>
          ) : (
            <>
              <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                {filteredTemplates.map((template) => (
                  <article
                    key={template.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {templateTypeLabel(template)}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {template.code} · Version {template.version}
                        </p>
                      </div>
                      {templateStatusBadge(template.status)}
                    </div>
                    <p className="text-xs text-gray-500 tabular-nums">
                      Effective {formatDate(template.effective_from)}
                    </p>
                    <div className="card-action-footer justify-end">
                      <TemplateActions template={template} {...actionProps} />
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="table table-fixed min-w-[820px]">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[20%]" />
                    <col className="w-[14%]" />
                    <col className="w-[18%]" />
                    <col className="w-[18%]" />
                  </colgroup>
                  <thead className="table-header">
                    <tr>
                      {['Template', 'Code', 'Status', 'Effective', 'Actions'].map((heading) => (
                        <th
                          key={heading}
                          className={`table-header-cell ${heading === 'Actions' ? 'text-right' : ''}`}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredTemplates.map((template) => (
                      <tr key={template.id} className="table-row">
                        <td className="table-cell">
                          <p className="font-medium text-gray-900 truncate">
                            {templateTypeLabel(template)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Version {template.version}
                          </p>
                        </td>
                        <td className="table-cell text-gray-600">
                          <p className="truncate">{template.code}</p>
                        </td>
                        <td className="table-cell">
                          {templateStatusBadge(template.status)}
                        </td>
                        <td className="table-cell text-gray-600 tabular-nums">
                          {formatDate(template.effective_from)}
                        </td>
                        <td className="table-cell text-right">
                          <TemplateActions template={template} {...actionProps} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      <TemplateEditor
        open={editor.open}
        template={editor.template}
        documentTypes={documentTypes}
        mergeFields={mergeFields}
        saving={save.isPending}
        onClose={() => {
          if (!save.isPending) setEditor({ open: false, template: null });
        }}
        onSave={saveTemplate}
      />
      <TemplatePreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
      <ConfirmModal
        isOpen={!!confirm}
        title={
          confirm?.type === 'publish'
            ? 'Publish this template?'
            : confirm?.type === 'archive'
              ? 'Archive this template?'
              : 'Delete this draft template?'
        }
        confirmLabel={
          confirm?.type === 'publish'
            ? 'Publish'
            : confirm?.type === 'archive'
              ? 'Archive'
              : 'Delete draft'
        }
        variant={confirm?.type === 'publish' ? 'primary' : 'danger'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
        isLoading={
          publish.isPending || archive.isPending || remove.isPending
        }
        theme="admin"
      >
        <p className="text-sm text-gray-700">
          {confirm?.type === 'publish'
            ? 'Employees can request letters from this version. Any older published version with the same code will be archived.'
            : confirm?.type === 'archive'
              ? 'Employees will no longer be able to create requests from this template.'
              : 'This removes the draft permanently. Published templates must be archived instead.'}
        </p>
      </ConfirmModal>
    </>
  );
};

const DocumentRequests = () => {
  const validTabs = ['queue', 'templates'];
  const [activeTab, setActiveTab] = useTabPersistence(
    'document-requests',
    'queue',
    validTabs,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { data = { requests: [] } } = useDocumentRequestQueue();
  const pendingCount = data.requests.filter(
    (request) => request.status === 'submitted',
  ).length;

  const changeTab = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    next.delete('id');
    next.delete('kind');
    setSearchParams(next);
  };

  return (
    <div
      className={
        activeTab === 'queue'
          ? 'flex flex-col gap-4 lg:h-[calc(100dvh-8rem)] lg:min-h-0 lg:overflow-hidden'
          : 'space-y-6'
      }
    >
      <div className="flex-shrink-0">
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 min-w-0 truncate">
            Document Requests
          </h1>
        </div>
        <p className="text-sm text-gray-500 ml-12">
          Review employee letter requests and control the templates used to issue them.
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth flex-shrink-0">
        <button
          type="button"
          onClick={() => changeTab('queue')}
          className={`tab-btn-admin ${activeTab === 'queue' ? 'active' : ''}`}
        >
          <FileText className="w-4 h-4" />
          Requests
          {pendingCount > 0 && (
            <CountBadge
              count={pendingCount}
              theme="admin"
              active={activeTab === 'queue'}
            />
          )}
        </button>
        <button
          type="button"
          onClick={() => changeTab('templates')}
          className={`tab-btn-admin ${activeTab === 'templates' ? 'active' : ''}`}
        >
          <Edit3 className="w-4 h-4" />
          Templates
        </button>
      </div>

      {activeTab === 'queue' ? <DocumentRequestQueue /> : <TemplatesTab />}
    </div>
  );
};

export default DocumentRequests;
